import PubNub from 'pubnub';
import { io } from 'socket.io-client';
import React from 'react';

// Environment variables
const USE_PUBNUB = true;
const PUBNUB_SUBSCRIBE_KEY = import.meta.env.VITE_APP_PUBNUB_SUBSCRIBE_KEY;
const SOCKET_SERVER_URL = import.meta.env.VITE_APP_SOCKET_SERVER_URL;

export default interface BaseModel {
    id: string;
    typename: string;
    eventStatus: null | string;
    eventResult: null | string;
    updatedBy: null | string;
    revisionId: number;
    updatedAt: string;
    createdAt: string;
}

export default class Sockets {
    private _userId: string;
    private _updateModelAttributes: any;
    private _pubnub: PubNub | null = null;
    private _socket: any = null;

    // ✅ MULTI EVENT LISTENERS
    private _eventListeners: Map<string, Set<(data: BaseModel) => void>> = new Map();

    static instance: any;

    constructor(userId: string, updateModelAttributes: any) {
        this._userId = userId;
        this._updateModelAttributes = updateModelAttributes;

        if (USE_PUBNUB) {
            this._pubnub = new PubNub({
                subscribeKey: PUBNUB_SUBSCRIBE_KEY,
                userId: this._userId,
                logVerbosity: false
            });

            this._pubnub.subscribe({ channels: [this._userId] });

            this._pubnub.addListener({
                status: (statusEvent) => {
                    if (statusEvent.category === 'PNConnectedCategory') {
                        console.log('[Sockets] PubNub Connected');
                    }
                },
                message: (messageEvent) => this.handleMessageEvent(messageEvent.message),
                presence: (presenceEvent) => console.log('Sockets::presence', presenceEvent)
            });
        } else {
            this._socket = io(`https://dev-api.northalley.com`, {
                path: '/socket-api/socket.io'
            });

            this._socket.on('connect', () => {
                this._socket.emit('join', this._userId);
                this._socket.emit('registerCustomer', { id: this._userId });
            });

            this._socket.on('message', this.handleMessageEvent.bind(this));
        }
    }

    // ✅ CENTRAL MESSAGE HANDLER
    private handleMessageEvent(message: any) {
        const updatedModelAttributes = message;
        if (!updatedModelAttributes) return;

        // system-level handling
        if (updatedModelAttributes.status === 'forcelogout') {
            const tokenValue = localStorage.getItem('serviceToken');
            if (updatedModelAttributes.accessToken !== tokenValue) {
                localStorage.removeItem('serviceToken');
                window.location.pathname = '/devicelimit';
                return;
            }
        }

        if (updatedModelAttributes.status === 'maintenance') {
            localStorage.removeItem('serviceToken');
            window.location.pathname = '/maintenance';
            return;
        }

        if (updatedModelAttributes.status === 'suspend') {
            localStorage.removeItem('serviceToken');
            location.reload();
            return;
        }

        // ✅ MAIN MODEL UPDATE
        if (updatedModelAttributes.id && updatedModelAttributes.typename) {
            this._updateModelAttributes?.(updatedModelAttributes);

            // ✅ EVENT-SPECIFIC LISTENERS
            const listeners = this._eventListeners.get(updatedModelAttributes.id);
            if (listeners) {
                listeners.forEach((cb) => cb(updatedModelAttributes as BaseModel));
            }
        }
    }

    // ✅ SUBSCRIBE TO RACE EVENT (NO HANDLER OVERWRITE)
    subscribeRaceEventId(eventId: string) {
        if (USE_PUBNUB && this._pubnub) {
            this._pubnub.subscribe({ channels: [this._userId, eventId] });
        } else if (this._socket) {
            this._socket.emit('join', eventId);
        }
    }

    // ✅ MULTI EVENT SUBSCRIBE
    subscribeEventId(eventId: string, listener: (data: BaseModel) => void) {
        if (!this._eventListeners.has(eventId)) {
            this._eventListeners.set(eventId, new Set());

            if (USE_PUBNUB && this._pubnub) {
                this._pubnub.subscribe({ channels: [eventId] });
            } else if (this._socket) {
                this._socket.emit('registerEvent', { id: eventId });
            }
        }

        this._eventListeners.get(eventId)!.add(listener);
    }

    // ✅ SAFE UNSUBSCRIBE
    unsubscribeEventId(eventId: string, listener?: (data: BaseModel) => void) {
        const listeners = this._eventListeners.get(eventId);
        if (!listeners) return;

        if (listener) {
            listeners.delete(listener);
        } else {
            listeners.clear();
        }

        if (listeners.size === 0) {
            this._eventListeners.delete(eventId);

            if (USE_PUBNUB && this._pubnub) {
                this._pubnub.unsubscribe({ channels: [eventId] });
            } else if (this._socket) {
                this._socket.emit('leave', eventId);
            }
        }
    }

    unsubscribe(channels: null | Array<string> = null) {
        if (USE_PUBNUB && this._pubnub) {
            channels ? this._pubnub.unsubscribe({ channels }) : this._pubnub.unsubscribeAll();
        } else if (this._socket) {
            channels ? channels.forEach((c) => this._socket.emit('leave', c)) : this._socket.disconnect();
        }
    }

    static getCurrentSocket() {
        return this.instance;
    }

    static setSocketForUser(userId: string, setModelFunction: any) {
        if (!this.instance) {
            this.instance = new Sockets(userId, setModelFunction);
        }
        return this.instance;
    }
}
